// Mock content data for Kapatia Pvt Ltd premium redesign (frontend-only)
import {
  Laptop, Server, Router, Building2, ShieldCheck, Truck,
  BadgeIndianRupee, Handshake, MonitorSmartphone, HardDrive,
  Network, Printer, Tv, AirVent, Cctv, Cpu
} from "lucide-react";

export const WHATSAPP = "919217399766";
export const PHONE = "+91 9217399766";
export const EMAIL = "info@kapatia.com";
export const CITIES = ["Noida", "Delhi", "Gurgaon", "Bangalore"];

export const WA_DEFAULT_MSG =
  "Hi Kapatia, I want to enquire about IT equipment. Please share details.";

export const wa = (text = WA_DEFAULT_MSG) =>
  `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(text)}`;

// Reads an ad-campaign tag from the URL (?c=... or ?utm_source=...) and remembers
// the first-touch value for the session, so tagged ad links can be attributed.
export const getCampaign = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("c") || params.get("utm_source") || params.get("campaign");
    if (fromUrl) {
      const clean = fromUrl.slice(0, 80);
      sessionStorage.setItem("kapatia_campaign", clean);
      return clean;
    }
    return sessionStorage.getItem("kapatia_campaign") || "";
  } catch (_) {
    return "";
  }
};

// Fire-and-forget log of an "Enquire on WhatsApp" click for interest analytics
export const logClick = (p = {}, source = "gallery") => {
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/product_clicks`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        product_id: p.id || "",
        code: p.code || p.sku || "",
        product_name: p.name || p.title || "",
        brand: p.brand || "",
        category: p.category || "",
        source,
        campaign: getCampaign(),
      }),
    }).catch(() => {});
  } catch (_) {}
};

export const navLinks = [
  { label: "Sell Assets", href: "#sell" },
  { label: "Buy Products", href: "#buy" },
  { label: "Stock", href: "#stock" },
  { label: "Estimate", href: "#estimate" },
  { label: "Buyback", href: "#buyback" },
  { label: "Brands", href: "#brands" },
  { label: "Contact", href: "#contact" }
];

export const heroChips = [
  "Desktops & laptops",
  "Servers & UPS",
  "D-Link / Cisco networking",
  "LED TVs & AC units"
];

export const slides = [
  { img: "https://customer-assets-4nw71qhi.emergentagent.net/job_kapatia-revamp/artifacts/mmnf9zlx_ChatGPT%20Image%20Aug%2013%2C%202026%2C%2002_39_59%20AM.png", caption: "Kapatia team — on-site IT asset dismantling" },
  { img: "https://customer-assets-4nw71qhi.emergentagent.net/job_kapatia-revamp/artifacts/lu2t6et0_01_desktops.webp", caption: "Desktops and workstations" },
  { img: "https://customer-assets-4nw71qhi.emergentagent.net/job_kapatia-revamp/artifacts/rslxeifd_image.png", caption: "Printers, scanners, IP phones and office equipment" },
  { img: "https://customer-assets-4nw71qhi.emergentagent.net/job_kapatia-revamp/artifacts/e3rbq0rz_06_corporate_it_assets.webp", caption: "Corporate IT asset buyback" },
  { img: "https://customer-assets-4nw71qhi.emergentagent.net/job_kapatia-revamp/artifacts/6ynmetha_ChatGPT%20Image%20Aug%2013%2C%202026%2C%2003_05_32%20PM.png", caption: "Cisco and D-Link networking products" },
  { img: "https://customer-assets-4nw71qhi.emergentagent.net/job_kapatia-revamp/artifacts/zm1s6u6c_ChatGPT%20Image%20Aug%2013%2C%202026%2C%2003_05_39%20PM.png", caption: "UPS and power backup systems" }
];

export const benefits = [
  { icon: Truck, title: "Free Site Visit", desc: "For bulk office and IT assets" },
  { icon: ShieldCheck, title: "Quick Pickup", desc: "Coordinated after inspection and pricing" },
  { icon: BadgeIndianRupee, title: "Competitive Pricing", desc: "Clear category-wise evaluation" },
  { icon: Handshake, title: "Hassle-Free Service", desc: "Business-focused communication" }
];

export const sellCategories = [
  { icon: Laptop, title: "Laptops & Desktops", desc: "Corporate laptops, MacBooks, desktops, AIO systems and workstations." },
  { icon: Server, title: "Servers & UPS", desc: "Servers, storage units, UPS systems, batteries and server-room equipment." },
  { icon: Router, title: "Networking Products", desc: "Switches, routers, access points, firewalls and racks." },
  { icon: Building2, title: "Office Equipment", desc: "LED TVs, AC units, printers, CCTV, monitors and office electronics." }
];

export const catalog = [
  { label: "Laptops", icon: Laptop },
  { label: "Desktops", icon: MonitorSmartphone },
  { label: "Workstations", icon: Cpu },
  { label: "Servers", icon: Server },
  { label: "Storage", icon: HardDrive },
  { label: "Networking", icon: Network },
  { label: "UPS", icon: ShieldCheck },
  { label: "Monitors", icon: MonitorSmartphone },
  { label: "Printers", icon: Printer },
  { label: "LED TVs", icon: Tv },
  { label: "AC Units", icon: AirVent },
  { label: "CCTV", icon: Cctv }
];

export const newProducts = [
  { brand: "HP | Lenovo | Dell", desc: "Laptops, desktops, workstations, monitors and accessories" },
  { brand: "Cisco | D-Link", desc: "Switches, routers, access points, firewalls and structured network products" },
  { brand: "APC", desc: "UPS systems, batteries, PDUs and power protection" },
  { brand: "LG | Samsung", desc: "Monitors, televisions, commercial displays and signage screens" },
  { brand: "Logitech", desc: "Keyboard, mouse, webcam, headset and video conferencing range" }
];

export const buybackSteps = [
  { n: "1", title: "Share asset list", desc: "Send category, quantity, photos, location and timeline." },
  { n: "2", title: "Get evaluation", desc: "Receive practical pricing based on condition and market value." },
  { n: "3", title: "Pickup and closure", desc: "Finalize pickup, payment and documentation terms clearly." }
];

export const brands = [
  { name: "HP", slug: "hp" },
  { name: "Lenovo", slug: "lenovo" },
  { name: "Dell", slug: "dell" },
  { name: "Cisco", slug: "cisco" },
  { name: "D-Link", slug: "dlink", logo: "/assets/icons/brand-dlink.svg" },
  { name: "APC", slug: "apc", logo: "/assets/icons/brand-apc.svg" },
  { name: "LG", slug: "lg" },
  { name: "Samsung", slug: "samsung" },
  { name: "Logitech", slug: "logitech", logo: "/assets/icons/brand-logitech.svg" }
];

export const stats = [
  { value: "4", label: "Cities served" },
  { value: "9+", label: "Global brands" },
  { value: "12+", label: "Asset categories" },
  { value: "B2B", label: "Bulk focus" }
];

// ---- Testimonials & trusted clients ----
export const clients = [
  "Fintech Corp", "MediCare Group", "EduSmart", "BuildWell Infra",
  "NovaBank", "RetailOne", "LogiTrans", "GreenEnergy Ltd",
  "CloudNine IT", "UrbanReality", "PrimeHealth", "AlphaWorks"
];

export const testimonials = [
  { name: "Rahul Mehta", role: "IT Manager, Fintech Corp", city: "Noida", initials: "RM",
    text: "Kapatia cleared 120+ laptops and desktops during our office upgrade. Pricing was fair and pickup was fully coordinated \u2014 zero hassle for my team." },
  { name: "Anita Sharma", role: "Admin Head, MediCare Group", city: "Delhi", initials: "AS",
    text: "We needed to dispose of old servers and UPS units quickly before relocation. The evaluation was transparent and payment terms were clear from day one." },
  { name: "Vikram Nair", role: "Procurement Lead, CloudNine IT", city: "Bangalore", initials: "VN",
    text: "Sourced 40 new HP and Dell machines through Kapatia with proper invoicing. Reliable communication and delivery matched what was promised." },
  { name: "Priya Deshmukh", role: "Facilities, BuildWell Infra", city: "Gurgaon", initials: "PD",
    text: "Full office dismantling and asset clearance handled professionally. They picked up displays, AC units and networking gear in a single organized visit." },
  { name: "Sameer Khan", role: "Founder, RetailOne", city: "Noida", initials: "SK",
    text: "Bought tested pre-owned monitors and desktops for our new branch at a great budget. Honest about condition \u2014 exactly as described." },
  { name: "Neha Gupta", role: "Ops Manager, LogiTrans", city: "Delhi", initials: "NG",
    text: "Quick response on WhatsApp, practical pricing and smooth buyback of our surplus IT inventory. Would work with them again." }
];

// ---- Price estimator (INDICATIVE ranges in INR per unit) ----
export const estimatorCategories = [
  { key: "laptop", label: "Laptop", min: 4000, max: 18000 },
  { key: "desktop", label: "Desktop / AIO", min: 3000, max: 12000 },
  { key: "workstation", label: "Workstation", min: 8000, max: 35000 },
  { key: "server", label: "Server", min: 15000, max: 120000 },
  { key: "monitor", label: "Monitor", min: 800, max: 5000 },
  { key: "printer", label: "Printer", min: 1000, max: 8000 },
  { key: "ups", label: "UPS", min: 1500, max: 20000 },
  { key: "networking", label: "Networking gear", min: 1000, max: 25000 },
  { key: "ledtv", label: "LED TV / Display", min: 3000, max: 25000 },
  { key: "ac", label: "AC Unit", min: 5000, max: 22000 }
];

export const conditions = [
  { key: "working", label: "Working", factor: 1.0 },
  { key: "minor", label: "Minor issues", factor: 0.65 },
  { key: "dead", label: "Not working / parts", factor: 0.3 }
];

// ---- Product gallery (sample pre-owned stock) ----
export const products = [
  { id: "p1", title: "Dell Latitude 5420", cat: "Laptop", specs: "i5 11th Gen \u00b7 16GB \u00b7 512GB SSD", condition: "Excellent", price: "\u20b924,500", img: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?auto=format&fit=crop&w=900&q=80" },
  { id: "p2", title: "Apple iMac 24\u2033 AIO", cat: "Desktop", specs: "M1 \u00b7 8GB \u00b7 256GB SSD", condition: "Very Good", price: "\u20b962,000", img: "https://images.unsplash.com/photo-1494173853739-c21f58b16055?auto=format&fit=crop&w=900&q=80" },
  { id: "p3", title: "Tower Server (Refurb)", cat: "Server", specs: "Xeon \u00b7 32GB ECC \u00b7 2\u00d71TB", condition: "Good", price: "\u20b984,000", img: "https://images.pexels.com/photos/17489151/pexels-photo-17489151.jpeg?auto=compress&cs=tinysrgb&w=900" },
  { id: "p4", title: "24\u2033 LED Monitor", cat: "Monitor", specs: "1080p \u00b7 IPS \u00b7 HDMI+VGA", condition: "Excellent", price: "\u20b93,800", img: "https://images.unsplash.com/photo-1587831990711-23ca6441447b?auto=format&fit=crop&w=900&q=80" },
  { id: "p5", title: "HP LaserJet MFP", cat: "Printer", specs: "Print \u00b7 Scan \u00b7 Copy \u00b7 Network", condition: "Very Good", price: "\u20b96,500", img: "https://images.unsplash.com/photo-1612815154858-60aa4c59eaa6?auto=format&fit=crop&w=900&q=80" },
  { id: "p6", title: "APC UPS 3kVA", cat: "UPS", specs: "Online \u00b7 With batteries", condition: "Good", price: "\u20b918,000", img: "https://images.pexels.com/photos/11708856/pexels-photo-11708856.jpeg?auto=compress&cs=tinysrgb&w=900" },
  { id: "p7", title: "Cisco 24-Port Switch", cat: "Networking", specs: "Gigabit \u00b7 Managed \u00b7 Rack", condition: "Excellent", price: "\u20b912,500", img: "https://images.pexels.com/photos/2881232/pexels-photo-2881232.jpeg?auto=compress&cs=tinysrgb&w=900" },
  { id: "p8", title: "55\u2033 Commercial Display", cat: "LED TV", specs: "4K \u00b7 Signage grade", condition: "Very Good", price: "\u20b921,000", img: "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=900&q=80" }
];

